import { useRef, useState } from 'react';

import { InviteStatus } from '@ndvr/auth-api';
import {
    AddressType,
    CountryCode,
    DataType,
    EmploymentStatus,
    MaritalStatus,
    PersonOrgProfile,
} from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { numberOfDependentsError, parseNumberOfDependents } from '@/utils/number-of-dependents';
import { TaxIdCell } from 'views/household/HouseholdMembersTab/TaxIdCell';
import { useInviteDetails } from 'views/person/person-queries';

import { AddressFields } from './AddressFields';
import {
    CheckBox,
    DateField,
    Field,
    FieldGroup,
    linkButtonClass,
    ReadField,
    SelectField,
    TextField,
    YesNoField,
} from './fields';
import {
    AddressValues,
    changedAddress,
    contactValue,
    COUNTRY_OPTIONS,
    data,
    DECLINED_CONTACT,
    formatAddress,
    isValidEmail,
    PersonKind,
    RELATION_LABELS,
    relationTo,
    requiredAddressParts,
    STANDALONE_CONTACT,
    StandaloneContactValues,
    text,
    toAddressValues,
    toContactChanges,
    todayInput,
    toProfileSaveRequest,
    toStandaloneContact,
    toStandaloneContactValues,
} from './onboarding-model';
import { useInvalidatePeople, useSaveOnboardingProfile } from './onboarding-tab-queries';
import { PersonCardProps, PersonCardShell } from './PersonCardShell';
import { useAutosave } from './useAutosave';

const MARITAL_OPTIONS = [
    { value: MaritalStatus.SINGLE, label: 'Single' },
    { value: MaritalStatus.MARRIED, label: 'Married' },
    { value: MaritalStatus.DIVORCED, label: 'Divorced' },
    { value: MaritalStatus.WIDOWED, label: 'Widowed' },
    { value: MaritalStatus.DOMESTIC_PARTNER, label: 'Domestic partner' },
];

const EMPLOYMENT_OPTIONS = [
    { value: EmploymentStatus.SALARIED_EMPLOYEE, label: 'Employed' },
    { value: EmploymentStatus.SELF_EMPLOYED, label: 'Self-employed' },
    { value: EmploymentStatus.RETIRED, label: 'Retired' },
    { value: EmploymentStatus.SEMI_RETIRED, label: 'Semi-retired' },
    { value: EmploymentStatus.HOMEMAKER, label: 'Homemaker' },
    { value: EmploymentStatus.UNEMPLOYED, label: 'Not employed' },
];

const WORKING: string[] = [EmploymentStatus.SALARIED_EMPLOYEE, EmploymentStatus.SELF_EMPLOYED];
const isWorking = (status: string) => WORKING.includes(status);

interface ClientValues {
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    phone: string;
    address: AddressValues;
    mailingDifferent: boolean;
    mailingAddress: AddressValues;
    dateOfBirth: string;
    usCitizen?: boolean;
    citizenship: string;
    maritalStatus: string;
    dependents: string;
    employment: string;
    occupation: string;
    employer: string;
    employerAddress: AddressValues;
    finraAffiliated?: boolean;
    finraFirm: string;
    /** The affiliated person: this client or another household member. */
    finraPerson: string;
    controlPerson?: boolean;
    controlCompany: string;
    controlTicker: string;
    financialFirmEmployee?: boolean;
    accredited?: boolean;
    trustedContact: string;
    standaloneContact: StandaloneContactValues;
}

const toValues = ({ personOrg, primaryAddress, mailingAddress, employerAddress }: PersonOrgProfile): ClientValues => ({
    firstName: text(personOrg.firstName),
    middleName: text(personOrg.middleName),
    lastName: text(personOrg.lastName),
    email: text(personOrg.primaryEmail),
    phone: text(personOrg.personalPhone),
    address: toAddressValues(primaryAddress),
    mailingDifferent: !!mailingAddress,
    mailingAddress: toAddressValues(mailingAddress),
    dateOfBirth: personOrg.dateOfBirth?.plainText ?? '',
    usCitizen: personOrg.currentUsCitizen ?? undefined,
    citizenship: personOrg.citizenshipCountries?.find((country) => country !== String(CountryCode.US)) ?? '',
    maritalStatus: personOrg.maritalStatus ?? '',
    dependents: personOrg.numberOfDependents?.toString() ?? '',
    employment: personOrg.employmentStatus?.[0] ?? '',
    occupation: personOrg.occupation ?? '',
    employer: personOrg.employerName ?? '',
    employerAddress: toAddressValues(employerAddress),
    finraAffiliated: personOrg.familyAffiliatedWithFinancialFirm ?? undefined,
    finraFirm: personOrg.familyAffiliatedWithFinancialFirmDetails ?? '',
    finraPerson: personOrg.affiliatedFamilyMemberGUID ?? '',
    controlPerson: personOrg.publicCompanyControlPerson ?? undefined,
    controlCompany: personOrg.publicCompanyName ?? '',
    controlTicker: personOrg.publicCompanyTicker ?? '',
    financialFirmEmployee: personOrg.employerAffiliatedWithFinancialFirm ?? undefined,
    accredited: personOrg.accreditedInvestor ?? undefined,
    trustedContact: contactValue(personOrg),
    standaloneContact: toStandaloneContactValues(personOrg),
});

/**
 * The citizenships to save. The card shows one answer, so the stored list keeps every country it does not touch: a
 * U.S. citizen keeps their other citizenships, and a foreign citizen keeps theirs after the one picked.
 */
const toCitizenships = (stored: CountryCode[], usCitizen: boolean | undefined, pickedValue: string) => {
    if (usCitizen === undefined) return stored.length ? stored : undefined;
    const picked = pickedValue as CountryCode | '';
    const others = stored.filter((country) => country !== CountryCode.US && country !== picked);
    const list = usCitizen
        ? [CountryCode.US, ...stored.filter((country) => country !== CountryCode.US)]
        : [...(picked ? [picked] : []), ...others];
    return list.length ? list : undefined;
};

/** The statuses to save: the picked one replaces the first, any further stored ones stay. */
const toEmploymentStatuses = (stored: EmploymentStatus[], pickedValue: string) => {
    const picked = pickedValue as EmploymentStatus | '';
    if (!picked) return stored.length ? stored : undefined;
    return [picked, ...stored.slice(1).filter((status) => status !== picked)];
};

/** The trusted contact choices: the household's people, and a contact entered by name in the portal, if stored. */
const contactOptions = (
    householdOptions: { value: string; label: string }[],
    standalone: StandaloneContactValues,
    hasStoredStandalone: boolean,
) => {
    const name = [standalone.firstName.trim(), standalone.lastName.trim()].filter(Boolean).join(' ');
    return [
        ...householdOptions,
        ...(hasStoredStandalone ? [{ value: STANDALONE_CONTACT, label: `${name} (not in household)` }] : []),
        { value: DECLINED_CONTACT, label: 'Declined to provide' },
    ];
};

/** The required fields still empty: the full Fidelity owner field set (Hub note B2.1). */
const countMissing = (values: ClientValues, taxIdProvided: boolean) => {
    const working = isWorking(values.employment);
    return [
        values.firstName,
        values.lastName,
        values.email,
        values.phone,
        ...requiredAddressParts(values.address),
        ...(values.mailingDifferent ? requiredAddressParts(values.mailingAddress) : []),
        values.dateOfBirth,
        taxIdProvided,
        values.usCitizen !== undefined,
        values.usCitizen !== false || values.citizenship,
        values.maritalStatus,
        values.employment,
        !working || values.employer,
        ...(working ? requiredAddressParts(values.employerAddress) : []),
        values.finraAffiliated !== undefined,
        !values.finraAffiliated || (values.finraFirm && values.finraPerson),
        values.controlPerson !== undefined,
        !values.controlPerson || (values.controlCompany && values.controlTicker),
        values.financialFirmEmployee !== undefined,
    ].filter((value) => !value).length;
};

export const ClientCard = (props: PersonCardProps) => {
    const { profile, householdGUID, household, addPerson } = props;
    const guid = profile.personOrgGUID;
    const [confirmLogin, setConfirmLogin] = useState(false);
    // The email the advisor agreed to make the new Client Portal login
    const loginConfirmedFor = useRef<string>();
    const [emailError, setEmailError] = useState<string>();
    const [dependentsError, setDependentsError] = useState<string>();
    const [contactEmailError, setContactEmailError] = useState<string>();
    const save = useSaveOnboardingProfile(householdGUID);
    const refreshPeople = useInvalidatePeople(householdGUID);
    const { data: invite } = useInviteDetails(guid);
    const storedEmail = text(profile.personOrg.primaryEmail);
    const registered = invite?.inviteStatus === InviteStatus.REGISTERED;

    const emailProblem = (email: string) => {
        if (!email && registered) return 'This person uses email to log in; it cannot be removed';
        return !email || isValidEmail(email) ? undefined : 'Enter a valid email address';
    };

    /** A standalone contact is stored only with first name, last name, email and phone. */
    const contactProblem = (form: ClientValues) => {
        if (form.trustedContact !== STANDALONE_CONTACT) return undefined;
        const { firstName, lastName, phone, email } = toStandaloneContact(form.standaloneContact);
        const emailInvalid = !!email && !isValidEmail(email);
        setContactEmailError(emailInvalid ? 'Enter a valid email address' : undefined);
        if (emailInvalid) return 'Enter a valid email address for the trusted contact';
        return firstName && lastName && phone && email
            ? undefined
            : 'Add the trusted contact’s first name, last name, phone and email';
    };

    /** Shows each problem at its field and returns the first one. */
    const check = (form: ClientValues) => {
        const email = form.email.trim();
        const emailMessage = emailProblem(email);
        setEmailError(emailMessage);
        const countError = numberOfDependentsError(form.dependents);
        setDependentsError(countError);
        const contactMessage = contactProblem(form);
        const problem = emailMessage ?? countError ?? contactMessage;
        if (problem) return problem;
        if (registered && email !== storedEmail && loginConfirmedFor.current !== email) {
            setConfirmLogin(true);
            return 'Confirm the Client Portal login change';
        }
        return undefined;
    };

    const { values, setValues, autosave } = useAutosave({
        stored: toValues(profile),
        check,
        save: (form) => {
            const working = isWorking(form.employment);
            return save
                .mutateAsync({
                    personOrgGUID: guid,
                    request: toProfileSaveRequest(
                        profile,
                        {
                            firstName: data(guid, DataType.PERSON_ORG_INFORMATION_FIRST_NAME, form.firstName),
                            middleName: data(guid, DataType.PERSON_ORG_INFORMATION_MIDDLE_NAME, form.middleName),
                            lastName: data(guid, DataType.PERSON_ORG_INFORMATION_LAST_NAME, form.lastName),
                            primaryEmail: data(guid, DataType.PERSON_ORG_EMAIL_ADDRESS, form.email),
                            personalPhone: data(guid, DataType.PERSON_ORG_PHONE_NUMBER, form.phone),
                            dateOfBirth: data(guid, DataType.PERSON_ORG_INFORMATION_DATE_OF_BIRTH, form.dateOfBirth),
                            currentUsCitizen: form.usCitizen,
                            citizenshipCountries: toCitizenships(
                                (profile.personOrg.citizenshipCountries ?? []) as CountryCode[],
                                form.usCitizen,
                                form.citizenship,
                            ),
                            maritalStatus: (form.maritalStatus as MaritalStatus) || undefined,
                            numberOfDependents: parseNumberOfDependents(form.dependents),
                            employmentStatus: toEmploymentStatuses(
                                (profile.personOrg.employmentStatus ?? []) as EmploymentStatus[],
                                form.employment,
                            ),
                            occupation: form.occupation.trim() || undefined,
                            employerName: working ? form.employer.trim() || undefined : undefined,
                            familyAffiliatedWithFinancialFirm: form.finraAffiliated,
                            familyAffiliatedWithFinancialFirmDetails: form.finraAffiliated
                                ? form.finraFirm.trim() || undefined
                                : undefined,
                            affiliatedFamilyMemberGUID: form.finraAffiliated
                                ? form.finraPerson || undefined
                                : undefined,
                            publicCompanyControlPerson: form.controlPerson,
                            publicCompanyName: form.controlPerson ? form.controlCompany.trim() || undefined : undefined,
                            publicCompanyTicker: form.controlPerson
                                ? form.controlTicker.trim() || undefined
                                : undefined,
                            employerAffiliatedWithFinancialFirm: form.financialFirmEmployee,
                            accreditedInvestor: form.accredited,
                            ...toContactChanges(profile.personOrg, form.trustedContact, form.standaloneContact),
                        },
                        {
                            primary: changedAddress(guid, AddressType.PRIMARY, form.address, profile.primaryAddress),
                            mailing: form.mailingDifferent
                                ? changedAddress(guid, AddressType.MAILING, form.mailingAddress, profile.mailingAddress)
                                : null,
                            employer: working
                                ? changedAddress(
                                      guid,
                                      AddressType.EMPLOYER,
                                      form.employerAddress,
                                      profile.employerAddress,
                                  )
                                : null,
                        },
                    ),
                })
                .then(() => setConfirmLogin(false));
        },
    });
    const set =
        <K extends keyof ClientValues>(key: K) =>
        (value: ClientValues[K]) =>
            setValues((current) => ({ ...current, [key]: value }));
    const setContact = (key: keyof StandaloneContactValues) => (value: string) =>
        setValues((current) => ({ ...current, standaloneContact: { ...current.standaloneContact, [key]: value } }));

    const working = isWorking(values.employment);
    const missing = countMissing(values, profile.personOrg.identifyingTaxIdProvided);

    const changeLogin = () => {
        loginConfirmedFor.current = values.email.trim();
        setConfirmLogin(false);
        autosave.flush();
    };

    const householdOptions = household
        .peopleOf(PersonKind.CLIENT, PersonKind.FAMILY)
        .filter((option) => option.value !== guid);
    const trustedContactOptions = contactOptions(
        householdOptions,
        values.standaloneContact,
        contactValue(profile.personOrg) === STANDALONE_CONTACT,
    );
    const affiliationOptions = [{ value: guid, label: `${household.nameOf(guid)} (self)` }, ...householdOptions];
    const contact = household.byGUID.get(values.trustedContact);
    const contactRelation =
        contact && household.primary
            ? relationTo(household.relations, household.primary.personOrgGUID, contact.personOrgGUID)
            : undefined;

    return (
        <PersonCardShell {...props} kind={PersonKind.CLIENT} missing={missing} autosave={autosave}>
            <FieldGroup title="Name" columns={3}>
                <TextField label="First" value={values.firstName} onChange={set('firstName')} required />
                <TextField label="Middle" value={values.middleName} onChange={set('middleName')} />
                <TextField label="Last" value={values.lastName} onChange={set('lastName')} required />
            </FieldGroup>

            <FieldGroup title="Contact">
                <TextField
                    label="Email"
                    value={values.email}
                    onChange={(value) => {
                        set('email')(value);
                        setEmailError(undefined);
                        setConfirmLogin(false);
                    }}
                    required={registered}
                    error={emailError}
                    helper={
                        registered
                            ? 'This email is used to log in to the Client Portal.'
                            : 'You can add email later. An email is needed to send an invitation.'
                    }
                />
                <TextField label="Mobile phone" value={values.phone} onChange={set('phone')} required />
                <AddressFields label="Legal address" value={values.address} onChange={set('address')} required />
                <div className="col-span-full">
                    <CheckBox
                        label="Mailing address is different"
                        checked={values.mailingDifferent}
                        onChange={set('mailingDifferent')}
                    />
                </div>
                {values.mailingDifferent && (
                    <AddressFields
                        label="Mailing address"
                        value={values.mailingAddress}
                        onChange={set('mailingAddress')}
                        required
                    />
                )}
                {confirmLogin && (
                    <div className="bg-sand-100 col-span-full flex flex-col gap-2 rounded px-3 py-2.5 text-[13px]">
                        <span>
                            Saving changes {values.firstName}’s Client Portal login to {values.email}.
                        </span>
                        <div className="flex gap-3.5">
                            <Button size="xs" onClick={changeLogin} disabled={save.isPending}>
                                Change login
                            </Button>
                            <button
                                type="button"
                                className="text-cocoa-600 cursor-pointer"
                                onClick={() => {
                                    setConfirmLogin(false);
                                    set('email')(storedEmail);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </FieldGroup>

            <FieldGroup title="Identity" columns={3}>
                <DateField
                    label="Date of birth"
                    value={values.dateOfBirth}
                    onChange={set('dateOfBirth')}
                    max={todayInput()}
                    required
                />
                <Field label="SSN" missing={!profile.personOrg.identifyingTaxIdProvided}>
                    {() => <TaxIdCell personOrg={profile.personOrg} onSaved={refreshPeople} required />}
                </Field>
                <SelectField
                    label="Citizenship"
                    value={values.usCitizen === undefined ? '' : values.usCitizen ? 'US' : 'FOREIGN'}
                    onChange={(value) => set('usCitizen')(value ? value === 'US' : undefined)}
                    options={[
                        { value: 'US', label: 'U.S.' },
                        { value: 'FOREIGN', label: 'Foreign' },
                    ]}
                    required
                />
                {values.usCitizen === false && (
                    <SelectField
                        label="Country of citizenship"
                        value={values.citizenship}
                        onChange={set('citizenship')}
                        options={COUNTRY_OPTIONS.filter((country) => country.value !== CountryCode.US)}
                        required
                    />
                )}
                <SelectField
                    label="Marital status"
                    value={values.maritalStatus}
                    onChange={set('maritalStatus')}
                    options={MARITAL_OPTIONS}
                    required
                />
                <TextField
                    label="Number of dependents"
                    value={values.dependents}
                    onChange={(value) => {
                        set('dependents')(value);
                        setDependentsError(numberOfDependentsError(value));
                    }}
                    error={dependentsError}
                />
                <SelectField
                    label="Employment status"
                    value={values.employment}
                    onChange={set('employment')}
                    options={EMPLOYMENT_OPTIONS}
                    required
                />
                <TextField label="Occupation" value={values.occupation} onChange={set('occupation')} />
                {working && (
                    <>
                        <TextField label="Employer" value={values.employer} onChange={set('employer')} required />
                        <AddressFields
                            label="Employer address"
                            value={values.employerAddress}
                            onChange={set('employerAddress')}
                            required
                        />
                    </>
                )}
            </FieldGroup>

            <FieldGroup title="Regulatory" columns={4}>
                <YesNoField
                    label="Associated with a FINRA member firm or exchange (self or household)"
                    value={values.finraAffiliated}
                    onChange={set('finraAffiliated')}
                    required
                />
                {values.finraAffiliated && (
                    <>
                        <SelectField
                            label="Who"
                            value={values.finraPerson}
                            onChange={set('finraPerson')}
                            options={affiliationOptions}
                            required
                        />
                        <TextField label="Firm name" value={values.finraFirm} onChange={set('finraFirm')} required />
                    </>
                )}
                <YesNoField
                    label="Control person of a public company (self or household)"
                    value={values.controlPerson}
                    onChange={set('controlPerson')}
                    required
                />
                {values.controlPerson && (
                    <>
                        <TextField
                            label="Company"
                            value={values.controlCompany}
                            onChange={set('controlCompany')}
                            required
                        />
                        <TextField
                            label="Ticker"
                            value={values.controlTicker}
                            onChange={set('controlTicker')}
                            required
                        />
                    </>
                )}
                <YesNoField
                    label="Employed by an RIA, broker-dealer or Fidelity"
                    value={values.financialFirmEmployee}
                    onChange={set('financialFirmEmployee')}
                    required
                />
                <YesNoField label="Accredited investor" value={values.accredited} onChange={set('accredited')} />
            </FieldGroup>

            <FieldGroup
                title="Trusted contact"
                action={
                    <button
                        type="button"
                        className={linkButtonClass}
                        onClick={() => addPerson(PersonKind.FAMILY, set('trustedContact'))}
                    >
                        Add
                    </button>
                }
            >
                <SelectField
                    label="Trusted contact"
                    value={values.trustedContact}
                    onChange={set('trustedContact')}
                    options={trustedContactOptions}
                    placeholder="Not asked yet"
                />
                {contact && (
                    <>
                        <ReadField
                            label="Relationship"
                            value={contactRelation ? RELATION_LABELS[contactRelation] : undefined}
                        />
                        <ReadField label="Phone" value={text(contact.personOrg.personalPhone)} />
                        <ReadField label="Email" value={text(contact.personOrg.primaryEmail)} />
                        <ReadField label="Address" value={formatAddress(toAddressValues(contact.primaryAddress))} />
                    </>
                )}
                {values.trustedContact === STANDALONE_CONTACT && (
                    <>
                        <TextField
                            label="First name"
                            value={values.standaloneContact.firstName}
                            onChange={setContact('firstName')}
                            required
                        />
                        <TextField
                            label="Last name"
                            value={values.standaloneContact.lastName}
                            onChange={setContact('lastName')}
                            required
                        />
                        <TextField
                            label="Relationship"
                            value={values.standaloneContact.relationship}
                            onChange={setContact('relationship')}
                        />
                        <TextField
                            label="Phone"
                            value={values.standaloneContact.phone}
                            onChange={setContact('phone')}
                            required
                        />
                        <TextField
                            label="Email"
                            value={values.standaloneContact.email}
                            onChange={(value) => {
                                setContact('email')(value);
                                setContactEmailError(undefined);
                            }}
                            required
                            error={contactEmailError}
                        />
                    </>
                )}
            </FieldGroup>
        </PersonCardShell>
    );
};
